import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  adminEventSettings,
  insurancePremiumCents,
  rateLabel,
  type AdminEventOutput,
  type AdminEventSettings,
  type InsuranceOffer,
} from '@gg/shared';
import { InsuranceCard } from '@/components/InsuranceCard';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Notice,
  Table,
  Td,
  TextField,
  Th,
} from '@/components/ui';
import { adminIssueKey } from '@/lib/adminErrors';
import { formatDateTime } from '@/lib/dates';
import { formatPrice, formatPriceExact } from '@/lib/i18n';
import { useAdminEvent, useAdminInsuranceStatements, useUpdateAdminEvent } from '@/lib/queries';
import { AdminMcpAccess } from './AdminMcpAccess';

type Values = {
  rate: string;
  termsFr: string;
  termsEn: string;
  orgName: string;
  orgEmail: string;
  countryCode: string;
  riskType: string;
  rulesUrl: string;
};
type Key = keyof AdminEventSettings;

/** Form field of each settings column, for errors and the change summary. */
const FIELD: Record<Key, keyof Values> = {
  insuranceRateBps: 'rate',
  insuranceTermsUrlFr: 'termsFr',
  insuranceTermsUrlEn: 'termsEn',
  insuranceOrgName: 'orgName',
  insuranceOrgEmail: 'orgEmail',
  countryCode: 'countryCode',
  insuranceRiskType: 'riskType',
  rulesUrl: 'rulesUrl',
};

const LABEL: Record<Key, string> = {
  insuranceRateBps: 'admin.settings.rate',
  insuranceTermsUrlFr: 'admin.settings.termsFr',
  insuranceTermsUrlEn: 'admin.settings.termsEn',
  insuranceOrgName: 'admin.settings.orgName',
  insuranceOrgEmail: 'admin.settings.orgEmail',
  countryCode: 'admin.settings.countryCode',
  insuranceRiskType: 'admin.settings.riskType',
  rulesUrl: 'admin.settings.rulesUrl',
};

/** Basket the preview prices the premium on. */
const SAMPLE_CENTS = 5000;

/** "17" or "17,5" → 1700 / 1750 basis points. Empty → null; anything else → NaN (refused). */
function parseRate(input: string): number | null {
  const s = input.trim().replace('%', '').replace(',', '.').trim();
  if (s === '') return null;
  return /^\d+(\.\d{1,2})?$/.test(s) ? Math.round(Number(s) * 100) : Number.NaN;
}

const toValues = (s: AdminEventSettings): Values => ({
  rate: s.insuranceRateBps === null ? '' : rateLabel(s.insuranceRateBps),
  termsFr: s.insuranceTermsUrlFr ?? '',
  termsEn: s.insuranceTermsUrlEn ?? '',
  orgName: s.insuranceOrgName ?? '',
  orgEmail: s.insuranceOrgEmail ?? '',
  countryCode: s.countryCode ?? '',
  riskType: s.insuranceRiskType ?? '',
  rulesUrl: s.rulesUrl ?? '',
});

const orNull = (s: string) => s.trim() || null;

const toSettings = (v: Values): AdminEventSettings => ({
  insuranceRateBps: parseRate(v.rate),
  insuranceTermsUrlFr: orNull(v.termsFr),
  insuranceTermsUrlEn: orNull(v.termsEn),
  insuranceOrgName: orNull(v.orgName),
  insuranceOrgEmail: orNull(v.orgEmail),
  countryCode: orNull(v.countryCode)?.toUpperCase() ?? null,
  insuranceRiskType: orNull(v.riskType),
  rulesUrl: orNull(v.rulesUrl),
});

/** The offer as checkout would show it from these settings, or null when none would show. */
function draftOffer(s: AdminEventSettings): InsuranceOffer | null {
  const rate = s.insuranceRateBps;
  if (rate === null || !Number.isInteger(rate) || rate <= 0 || !s.insuranceTermsUrlFr) return null;
  return { rateBps: rate, termsUrlFr: s.insuranceTermsUrlFr, termsUrlEn: s.insuranceTermsUrlEn };
}

function display(t: TFunction, s: AdminEventSettings, k: Key): string {
  const v = s[k];
  if (v === null) return t('admin.settings.empty');
  return k === 'insuranceRateBps' ? `${rateLabel(v as number)} %` : String(v);
}

/**
 * A-13: event settings: the Beticketing insurance (decision 0025), terms and rules links. Below
 * them, MCP access (issue #47, decision 0030).
 */
export function AdminSettings() {
  const { t } = useTranslation();
  const event = useAdminEvent();

  if (event.isLoading) return <p className="text-center text-ink-muted">…</p>;
  if (event.isError || !event.data)
    return (
      <p role="alert" className="text-danger">
        {t('admin.settings.saveError')}
      </p>
    );
  return (
    <div className="space-y-6">
      <SettingsForm data={event.data} />
      <AdminMcpAccess />
    </div>
  );
}

function SettingsForm({ data }: { data: AdminEventOutput }) {
  const { t } = useTranslation();
  const save = useUpdateAdminEvent();
  const { register, handleSubmit, reset, setError, watch, formState } = useForm<Values>({
    defaultValues: toValues(data.settings),
  });
  const [pending, setPending] = useState<{ next: AdminEventSettings; changed: Key[] } | null>(null);
  const [notice, setNotice] = useState<'saved' | 'nothing' | null>(null);

  const current = data.settings;
  const offer = draftOffer(toSettings(watch()));

  const onSubmit = (v: Values) => {
    setNotice(null);
    const parsed = adminEventSettings.safeParse(toSettings(v));
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = FIELD[issue.path[0] as Key];
        if (field) setError(field, { message: t(`admin.error.${adminIssueKey(issue)}`) });
      }
      return;
    }
    const changed = (Object.keys(FIELD) as Key[]).filter((k) => parsed.data[k] !== current[k]);
    if (!changed.length) return setNotice('nothing');
    setPending({ next: parsed.data, changed });
  };

  const confirm = async () => {
    if (!pending) return;
    try {
      const saved = await save.mutateAsync(pending.next);
      reset(toValues(saved.settings));
      setPending(null);
      setNotice('saved');
    } catch {
      setPending(null); // Shown through save.isError.
    }
  };

  const err = (f: keyof Values) => formState.errors[f]?.message;
  const status = data.insuranceOnSale
    ? { tone: 'success' as const, label: t('admin.settings.insurance.onSale') }
    : data.insuranceBlocker === 'off' || data.insuranceBlocker === null
      ? { tone: 'neutral' as const, label: t('admin.settings.insurance.off') }
      : { tone: 'danger' as const, label: t(`admin.settings.insurance.${data.insuranceBlocker}`) };

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <h1 className="font-display text-section-lg font-bold">{t('admin.settings.title')}</h1>
        <p className="text-sm text-ink-muted">{t('admin.settings.intro')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
          <Card className="gap-4!">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold">{t('admin.settings.insurance.title')}</h2>
              <Badge tone={status.tone}>{status.label}</Badge>
            </div>
            <p className="m-0 text-sm text-ink-muted">{t('admin.settings.insurance.intro')}</p>
            <TextField
              label={t('admin.settings.rate')}
              hint={t('admin.settings.rateHint')}
              inputMode="decimal"
              error={err('rate')}
              {...register('rate')}
            />
            <TextField
              type="url"
              label={t('admin.settings.termsFr')}
              placeholder="https://"
              error={err('termsFr')}
              {...register('termsFr')}
            />
            <TextField
              type="url"
              label={t('admin.settings.termsEn')}
              hint={t('admin.settings.termsEnHint')}
              placeholder="https://"
              error={err('termsEn')}
              {...register('termsEn')}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={t('admin.settings.orgName')}
                error={err('orgName')}
                {...register('orgName')}
              />
              <TextField
                type="email"
                label={t('admin.settings.orgEmail')}
                error={err('orgEmail')}
                {...register('orgEmail')}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
              <TextField
                label={t('admin.settings.countryCode')}
                maxLength={2}
                autoCapitalize="characters"
                error={err('countryCode')}
                {...register('countryCode')}
              />
              <TextField
                label={t('admin.settings.riskType')}
                hint={t('admin.settings.riskTypeHint')}
                error={err('riskType')}
                {...register('riskType')}
              />
            </div>
            <Notice>
              {data.insuranceRiskClosedAt
                ? t('admin.settings.risk.closedAt', {
                    date: formatDateTime(data.insuranceRiskClosedAt),
                  })
                : t('admin.settings.risk.openUntil')}
              {data.insurancePendingDeclarations > 0 && (
                <>
                  {' '}
                  {t('admin.settings.risk.pending', { count: data.insurancePendingDeclarations })}
                </>
              )}
            </Notice>
          </Card>

          <Card className="gap-3! self-start">
            <h2 className="text-lg font-bold">{t('admin.settings.preview.title')}</h2>
            {offer ? (
              <>
                <p className="m-0 text-sm text-ink-muted">
                  {t('admin.settings.preview.sample', {
                    amount: formatPrice(SAMPLE_CENTS),
                    premium: formatPriceExact(insurancePremiumCents(SAMPLE_CENTS, offer.rateBps)),
                  })}
                </p>
                <InsuranceCard
                  offer={offer}
                  insuredCents={SAMPLE_CENTS}
                  checked={false}
                  onChange={() => {}}
                  holder={{ firstName: '', lastName: '' }}
                />
              </>
            ) : (
              <Notice>{t('admin.settings.preview.none')}</Notice>
            )}
          </Card>
        </div>

        <Card className="gap-4!">
          <h2 className="text-lg font-bold">{t('admin.settings.legal.title')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label={t('admin.settings.rulesUrl')}
              placeholder="/reglement"
              hint={t('admin.settings.rulesUrlHint')}
              error={err('rulesUrl')}
              {...register('rulesUrl')}
            />
          </div>
        </Card>

        <div className="flex items-center gap-4">
          <Button type="submit" size="md" className="w-auto!" loading={save.isPending}>
            {t('admin.settings.save')}
          </Button>
          {notice && (
            <span role="status" className="text-sm text-ink-muted">
              {t(`admin.settings.${notice}`)}
            </span>
          )}
          {save.isError && (
            <span role="alert" className="text-sm text-danger">
              {t('admin.settings.saveError')}
            </span>
          )}
        </div>
      </form>

      {data.insuranceBlocker !== 'not_configured' && current.insuranceRateBps !== null && (
        <Statements />
      )}

      <ConfirmDialog
        open={pending !== null}
        title={t('admin.settings.confirm.title')}
        confirmLabel={t('admin.confirm.apply')}
        cancelLabel={t('admin.confirm.cancel')}
        loading={save.isPending}
        onConfirm={confirm}
        onCancel={() => setPending(null)}
      >
        {pending && (
          <div className="flex flex-col gap-3">
            <p className="m-0">{t('admin.settings.confirm.body')}</p>
            {current.insuranceRateBps === null && pending.next.insuranceRateBps !== null && (
              <p className="m-0 font-semibold">{t('admin.settings.confirm.offOn')}</p>
            )}
            {current.insuranceRateBps !== null && pending.next.insuranceRateBps === null && (
              <p className="m-0 font-semibold">{t('admin.settings.confirm.onOff')}</p>
            )}
            <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-sm">
              {pending.changed.map((k) => (
                <li key={k}>
                  {t(LABEL[k])}
                  {t('admin.settings.colon')}
                  <span className="text-ink-muted">{display(t, current, k)}</span> →{' '}
                  <span className="font-semibold break-all">{display(t, pending.next, k)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ConfirmDialog>
    </section>
  );
}

/** The insurer's unpaid premium statements, to pay monthly (API §1.5 c, decision 0025). */
function Statements() {
  const { t } = useTranslation();
  const statements = useAdminInsuranceStatements(true);
  return (
    <Card className="gap-3!">
      <h2 className="text-lg font-bold">{t('admin.settings.statements.title')}</h2>
      <p className="m-0 text-sm text-ink-muted">{t('admin.settings.statements.intro')}</p>
      {statements.isLoading ? (
        <p className="m-0 text-sm text-ink-muted">…</p>
      ) : statements.isError ? (
        <Notice>{t('admin.settings.statements.error')}</Notice>
      ) : statements.data?.statements.length ? (
        <Table>
          <thead>
            <tr>
              <Th>{t('admin.settings.statements.reference')}</Th>
              <Th className="text-right">{t('admin.settings.statements.total')}</Th>
            </tr>
          </thead>
          <tbody>
            {statements.data.statements.map((s) => (
              <tr key={s.id}>
                <Td className="font-semibold">{s.reference}</Td>
                <Td className="text-right tabular-nums">{formatPriceExact(s.totalCents)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="m-0 text-sm text-ink-muted">{t('admin.settings.statements.none')}</p>
      )}
    </Card>
  );
}
